import type { ReactNode } from "react";
import { cn } from "~/utils";
import { Text } from "~/components/text";

export interface ChartPanelProps {
  /** Small leading icon, e.g. a Lucide icon. */
  icon?: ReactNode;
  title: ReactNode;
  /** Optional badge shown next to the title, e.g. an "Earliest data" pill. */
  titleBadge?: ReactNode;
  subtitle?: ReactNode;
  /** Top-right controls, e.g. a granularity toggle. */
  actions?: ReactNode;
  /** Optional legend rendered top-right between the header and the body. */
  legend?: ReactNode;
  /** Optional footer rendered below a divider, e.g. period totals. */
  footer?: ReactNode;
  children: ReactNode;
  className?: string;
  testId?: string;
}

/**
 * Shared shell for a chart panel: header (icon, title, subtitle, actions), an
 * optional legend, the chart body, and an optional footer. Reused by every sales
 * panel (Total Sales, Amazon Sales) so they stay visually consistent.
 */
export const ChartPanel = ({
  icon,
  title,
  titleBadge,
  subtitle,
  actions,
  legend,
  footer,
  children,
  className,
  testId,
}: ChartPanelProps) => (
  <section
    data-testid={testId}
    className={cn(
      "rounded-xl border border-border bg-background p-4",
      className,
    )}
  >
    <header className="flex items-start justify-between gap-3">
      <div className="flex items-center gap-2.5">
        {icon && (
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-500">
            {icon}
          </span>
        )}
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <Text as="h3" variant="title">
              {title}
            </Text>
            {titleBadge}
          </div>
          {subtitle && (
            <Text variant="metricCaption" className="mt-0.5">
              {subtitle}
            </Text>
          )}
        </div>
      </div>
      {actions && <div className="shrink-0">{actions}</div>}
    </header>

    {legend && <div className="mt-2 flex justify-end">{legend}</div>}

    <div className="mt-3">{children}</div>

    {footer && <div className="mt-3 border-t border-border pt-3">{footer}</div>}
  </section>
);
